import { useMemo, useState, type ReactElement } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  fetchPivot,
  REPORT_DIMENSIONS,
  type PivotRow,
  type ReportDimension,
} from '../../api/admin.js';
import { nanoToUsd, nanoToUsdCompact, formatNumber } from '../../lib/format.js';
import type { CsvColumn } from '../../lib/csv.js';
import { ReportFilters, useReportFilters } from './components/ReportFilters.js';
import { ExportCsvButton } from './components/ExportCsvButton.js';
import { PivotTable, orderRowKeys } from './components/PivotTable.js';
import { MetricChart, type MetricSeries } from './components/MetricChart.js';

const NONE = 'none' as const;
type ColDimChoice = ReportDimension | typeof NONE;

const TEMPORAL_DIMS: ReadonlySet<string> = new Set(['day', 'week', 'month']);

const CSV_COLUMNS: CsvColumn<PivotRow>[] = [
  { header: 'row', value: (r) => r.rowKey },
  { header: 'col', value: (r) => r.colKey },
  { header: 'cost_usd', value: (r) => nanoToUsd(r.costNano) },
  { header: 'cost_nano', value: (r) => r.costNano },
  { header: 'calls', value: (r) => r.callCount },
  { header: 'errors', value: (r) => r.errorCount },
];

/** Drill-down: sólo user/module mapean a filtros de /sessions. */
function makeDrillHref(
  rowDim: ReportDimension,
  colDim: ColDimChoice,
): ((rk: string | null, ck: string | null) => string | null) | undefined {
  const dims = colDim === NONE ? [rowDim] : [rowDim, colDim];
  if (!dims.some((d) => d === 'user' || d === 'module')) return undefined;
  return (rk, ck) => {
    const p = new URLSearchParams();
    const assign = (dim: ReportDimension, val: string | null): void => {
      if (val === null) return;
      if (dim === 'user') p.set('user', val);
      if (dim === 'module') p.set('module', val);
    };
    assign(rowDim, rk);
    if (colDim !== NONE) assign(colDim, ck);
    const qs = p.toString();
    return qs ? `/sessions?${qs}` : null;
  };
}

export function PivotReportPage(): ReactElement {
  const filters = useReportFilters();
  const [rowDim, setRowDim] = useState<ReportDimension>('day');
  const [colDim, setColDim] = useState<ColDimChoice>('model');

  const query = useQuery({
    queryKey: ['admin', 'reports', 'pivot', { rowDim, colDim, f: filters.key }],
    queryFn: () =>
      fetchPivot({
        rowDim,
        colDim: colDim === NONE ? null : colDim,
        filters: filters.toParams(),
      }),
  });

  const rows = query.data?.rows ?? [];
  const temporalRow = TEMPORAL_DIMS.has(rowDim);

  // El chart usa EXACTAMENTE el mismo orden de filas que la tabla
  // (orderRowKeys) para que ambos coincidan siempre.
  const chart = useMemo(() => {
    const rowSet = new Set<string>();
    const colSet = new Set<string>();
    const map = new Map<string, number>();
    const rowTotalNano = new Map<string, bigint>();
    for (const r of rows) {
      const rk = r.rowKey ?? '∅';
      const ck = r.colKey ?? '∅';
      rowSet.add(rk);
      colSet.add(ck);
      const nano = BigInt(r.costNano);
      map.set(`${rk}|${ck}`, Number(nano));
      rowTotalNano.set(rk, (rowTotalNano.get(rk) ?? 0n) + nano);
    }
    const cats = orderRowKeys(rowSet, rowTotalNano, temporalRow);
    const series: MetricSeries[] = [...colSet].sort().map((ck) => ({
      key: ck,
      values: cats.map((rk) => map.get(`${rk}|${ck}`) ?? 0),
    }));
    return { categories: cats, series };
  }, [rows, temporalRow]);

  const grandTotalNano = useMemo(
    () => rows.reduce((a, r) => a + BigInt(r.costNano), 0n).toString(),
    [rows],
  );

  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="page-title">Pivot · consumo</h1>
          <p className="page-sub">
            Gasto real (provider cost) · {rowDim}
            {colDim !== NONE ? ` × ${colDim}` : ''} ·{' '}
            <span className="mono">
              {filters.from} → {filters.to}
            </span>{' '}
            · <span className="mono">{nanoToUsdCompact(grandTotalNano)}</span> total
          </p>
        </div>
        <ExportCsvButton filename={`pivot-${rowDim}-${colDim}`} rows={rows} columns={CSV_COLUMNS} />
      </div>

      <div className="filterbar">
        <DimensionPicker label="filas" value={rowDim} onChange={(d) => setRowDim(d as ReportDimension)} options={REPORT_DIMENSIONS} />
        <DimensionPicker
          label="columnas"
          value={colDim}
          onChange={(d) => setColDim(d as ColDimChoice)}
          options={[NONE, ...REPORT_DIMENSIONS]}
        />
      </div>
      <ReportFilters state={filters} />

      {query.isPending ? (
        <div className="loading">Cargando…</div>
      ) : query.isError ? (
        <div className="loading">Error: {query.error.message}</div>
      ) : (
        <div className="pad-7">
          <section className="card" style={{ marginBottom: 24 }}>
            <header className="card-head">
              <h3 className="card-title">Gasto apilado por {rowDim}</h3>
            </header>
            <MetricChart
              categories={chart.categories}
              series={chart.series}
              formatValue={(n) => nanoToUsdCompact(BigInt(Math.round(n)).toString())}
            />
          </section>
          <section className="card">
            <header className="card-head">
              <h3 className="card-title">Breakdown</h3>
              <span className="mono tt-sec">{formatNumber(rows.length)} celdas</span>
            </header>
            <PivotTable
              rows={rows}
              rowLabel={rowDim}
              colLabel={colDim === NONE ? null : colDim}
              temporalRow={temporalRow}
              drillHref={makeDrillHref(rowDim, colDim)}
            />
          </section>
        </div>
      )}
    </>
  );
}

interface DimensionPickerProps {
  label: string;
  value: string;
  options: readonly string[];
  onChange: (d: string) => void;
}

function DimensionPicker({ label, value, options, onChange }: DimensionPickerProps): ReactElement {
  return (
    <div className="group">
      <span className="label" style={{ margin: 0 }}>
        {label}
      </span>
      <div className="chip-group">
        {options.map((d) => (
          <button
            key={d}
            type="button"
            className={`chip${value === d ? ' is-on' : ''}`}
            onClick={() => onChange(d)}
            data-testid={`pivot-${label}-${d}`}
          >
            {d}
          </button>
        ))}
      </div>
    </div>
  );
}
